import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import "./styles/AdminMessages.css";
import "./styles/AdminOrders.css";
import { getImageUrl } from "../../utility/imageHelper";
import { Link } from "react-router-dom";
import { slugify } from "../../utility/slugify";

const ORDER_STATUSES = ["paid", "shipped", "complete", "cancelled"];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatusFilters, setActiveStatusFilters] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [shippingInputs, setShippingInputs] = useState({});

  const fetchOrders = async () => {
    try {
      const response = await axios.get("/api/admin/orders");
      setOrders(response.data.orders);
    } catch (error) {
      console.error("🔥 Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const toggleRow = (orderId) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const getCsrfToken = () => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];
  };

  const handleStatusChange = async (order_uuid, newStatus) => {
    try {
      const csrfToken = getCsrfToken();
      await axios.put(
        `/api/admin/orders/${order_uuid}/status`,
        { status: newStatus },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      setOrders((prev) =>
        prev.map((o) =>
          o.order_uuid === order_uuid ? { ...o, status: newStatus } : o
        )
      );
    } catch (error) {
      console.error("🔥 Error updating order status:", error);
    }
  };

  const handleShippingUpdate = async (order_uuid) => {
    try {
      const csrfToken = getCsrfToken();
      const order = orders.find((o) => o.order_uuid === order_uuid);
      const tracking_number =
        shippingInputs[order_uuid]?.tracking_number ?? order.tracking_number;
      const carrier =
        shippingInputs[order_uuid]?.carrier ?? order.carrier;

      if (!tracking_number || !carrier) {
        return alert("Please enter a tracking number and select a carrier.");
      }

      await axios.put(
        `/api/admin/orders/${order_uuid}/shipping`,
        { tracking_number, carrier },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      setOrders((prev) =>
        prev.map((o) =>
          o.order_uuid === order_uuid
            ? { ...o, tracking_number, carrier }
            : o
        )
      );
    } catch (error) {
      console.error("🔥 Error updating shipping:", error);
    }
  };

  const handleArchiveOrder = async (order_uuid, archived) => {
    try {
      const csrfToken = getCsrfToken();
      await axios.put(
        `/api/admin/orders/${order_uuid}/archive`,
        { archived: !archived },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      setOrders((prev) =>
        prev.map((o) =>
          o.order_uuid === order_uuid ? { ...o, archived: !archived } : o
        )
      );
    } catch (error) {
      console.error("🔥 Error archiving order:", error);
    }
  };

  // Filtering for ACTIVE orders:
  const activeOrders = orders.filter((o) => !o.archived);

  // Filtering for ARCHIVED orders:
  const archivedOrders = orders.filter((o) => o.archived);

  // Apply status filters only to active orders
  const filteredActiveOrders =
    activeStatusFilters.length === 0
      ? activeOrders
      : activeOrders.filter((order) =>
          activeStatusFilters.includes(order.status)
        );

  return (
    <div className="admin-messages-container">
      <h2>All Orders</h2>

      {/* Status Filters */}
      <div className="order-status-filters">
        {ORDER_STATUSES.map((status) => (
          <button
            key={status}
            className={`status-pill ${
              activeStatusFilters.includes(status) ? "active" : ""
            }`}
            onClick={() =>
              setActiveStatusFilters((prev) =>
                prev.includes(status)
                  ? prev.filter((s) => s !== status)
                  : [...prev, status]
              )
            }
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* ACTIVE ORDERS */}
      <h3 className="table-section-title">Active Orders</h3>
      {loading ? (
        <p>Loading orders...</p>
      ) : filteredActiveOrders.length === 0 ? (
        <p>No active orders found.</p>
      ) : (
        <div className="table-wrapper">
          <table className="messages-table">
            <thead>
              <tr>
                <th>More</th>
                <th>Order ID</th>
                <th>Total Price</th>
                <th>Status</th>
                <th>Placed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActiveOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr className={order.archived ? "archived-row" : ""}>
                    <td>
                      <button
                        className={`expand-btn ${
                          expandedRows.includes(order.id) ? "expanded" : ""
                        }`}
                        onClick={() => toggleRow(order.id)}
                      >
                        <p>⬇️</p>
                      </button>
                    </td>
                    <td>{order.order_uuid}</td>
                    <td>${order.total_price.toFixed(2)}</td>
                    <td>
                      <select
                        className="role-select"
                        value={order.status}
                        onChange={(e) =>
                          handleStatusChange(order.order_uuid, e.target.value)
                        }
                      >
                        <option value="paid">Paid</option>
                        <option value="shipped">Shipped</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="complete">Complete</option>
                      </select>
                    </td>
                    <td>{new Date(order.created_at).toLocaleString()}</td>

                    {/* ARCHIVE */}
                    <td className="table-actions">
                      <button
                        className="btn archive-btn"
                        onClick={() =>
                          handleArchiveOrder(order.order_uuid, order.archived)
                        }
                      >
                        <i className="fa-solid fa-box-archive"></i> Archive
                      </button>
                    </td>
                  </tr>

                  {/* Drawer / Expanded Row */}
                  <tr className="order-details-row">
                    <td colSpan="6">
                      <div
                        className={`order-details-wrapper ${
                          expandedRows.includes(order.id) ? "expanded" : ""
                        }`}
                      >
                        {/* SHIPPING / DETAILS */}
                        <div className="order-details">
                          <div className="order-shipping-info">
                            <h3>Shipping Info</h3>
                            <div>
                              <strong>Order Placed By:</strong>{" "}
                              {order.user_id ? "Registered User" : "Guest"}
                            </div>
                            <div>
                              <strong>Email:</strong> {order.email}
                            </div>
                            <div>
                              <strong>Name:</strong> {order.name}
                            </div>
                            <div>
                              <strong>Shipping Address:</strong>{" "}
                              {order.shipping_address}
                            </div>
                            <div>
                              <strong>Insurance:</strong>{" "}
                              <span
                                style={{
                                  cursor: "unset",
                                  fontSize: "16px",
                                  padding: "0.2rem 0.8rem",
                                }}
                                className={
                                  order.shipping_insurance
                                    ? "status-pill active"
                                    : "status-pill"
                                }
                              >
                                {order.shipping_insurance ? "Yes" : "No"}
                              </span>
                            </div>
                          </div>

                          {/* TRACKING */}
                          <div className="order-shipping-info">
                            <h3>Tracking Info</h3>

                            <div>
                              <strong>Carrier:</strong>
                              <div className="carrier-buttons">
                                {["USPS", "UPS", "FedEx"].map((carrierOption) => (
                                  <button
                                    key={carrierOption}
                                    className={`carrier-button ${
                                      (shippingInputs[order.order_uuid]?.carrier ||
                                        order.carrier) === carrierOption
                                        ? "active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setShippingInputs((prev) => ({
                                        ...prev,
                                        [order.order_uuid]: {
                                          ...(prev[order.order_uuid] || {}),
                                          carrier: carrierOption,
                                        },
                                      }))
                                    }
                                  >
                                    {carrierOption}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="tracking-input">
                              <strong>Tracking Number:</strong>
                              <input
                                type="text"
                                value={
                                  shippingInputs[order.order_uuid]?.tracking_number ??
                                  order.tracking_number ??
                                  ""
                                }
                                onChange={(e) =>
                                  setShippingInputs((prev) => ({
                                    ...prev,
                                    [order.order_uuid]: {
                                      ...(prev[order.order_uuid] || {}),
                                      tracking_number: e.target.value,
                                    },
                                  }))
                                }
                              />
                            </div>

                            <button
                              onClick={() =>
                                handleShippingUpdate(order.order_uuid)
                              }
                              className="tracking-submit-btn"
                            >
                              Save
                            </button>
                          </div>
                        </div>

                        {/* PRODUCTS */}
                        {order.products.length > 0 ? (
                          <div className="order-shipping-info">
                            <h3>Products in Order</h3>
                            <ul>
                              {order.products.map((product) => (
                                <Link
                                  className="orders-view-product-page"
                                  key={product.product_id}
                                  to={`/shop-knives/knife/${product.product_id}/${slugify(
                                    product.name
                                  )}`}
                                >
                                  <li className="order-product">
                                    <img
                                      src={
                                        product.image_url
                                          ? getImageUrl(product.image_url)
                                          : "/default-product.jpg"
                                      }
                                      alt={product.name}
                                      className="order-product-img"
                                    />

                                    <div className="order-product-details">
                                      <span className="product-name">
                                        {product.name} (#{product.product_id})
                                      </span>
                                      <span className="product-qty">
                                        Qty: {product.quantity}
                                      </span>
                                      <span className="product-price">
                                        ${product.price.toFixed(2)}
                                      </span>
                                    </div>
                                  </li>
                                </Link>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>No products found for this order.</p>
                        )}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ARCHIVED ORDERS */}
      <h3 className="table-section-title">Archived Orders</h3>
      {archivedOrders.length === 0 ? (
        <p>No archived orders.</p>
      ) : (
        <div className="table-wrapper">
          <table className="messages-table archived">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Total Price</th>
                <th>Status</th>
                <th>Placed At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {archivedOrders.map((order) => (
                <tr key={order.order_uuid} className="archived-row">
                  <td>{order.order_uuid}</td>
                  <td>${order.total_price.toFixed(2)}</td>
                  <td>{order.status}</td>
                  <td>{new Date(order.created_at).toLocaleString()}</td>

                  <td className="table-actions">
                    <button
                      className="btn archive-btn"
                      onClick={() =>
                        handleArchiveOrder(order.order_uuid, order.archived)
                      }
                    >
                      <i className="fa-solid fa-box-archive"></i> Unarchive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;